import { useMemo, useState } from "react";
import { useGetIdentity, useGetList } from "ra-core";
import { formatDistanceToNowStrict } from "date-fns";
import { fr } from "date-fns/locale";
import { Link } from "react-router";
import { Check, Code2, Plus } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { StatusPill } from "@/components/atomic-crm/ui";
import type { StatusTone } from "@/components/atomic-crm/ui";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { EmptyState } from "../shared/EmptyState";
import type { DevTask } from "../types";

// Sprint 7 T7.5 — TicketsCard refondu conforme à design/atomic-crm/
// today-dev.jsx (composant TicketsCard). Filter tabs + tri par statut
// (review → wip → todo → waiting → done) + header avec count + bouton
// "Nouveau". Ligne de ticket enrichie : dot status couleur + [REF] +
// titre + meta multi-info + due relative.

const ACTIVE_STATUSES =
  "(backlog,todo,in-progress,in-review,en_attente_client)";

// Mapping DB → buckets UI maquette
type Bucket = "todo" | "wip" | "review" | "waiting" | "done";

const DB_TO_BUCKET: Record<string, Bucket> = {
  backlog: "todo",
  todo: "todo",
  "in-progress": "wip",
  "in-review": "review",
  en_attente_client: "waiting",
  done: "done",
  canceled: "done",
};

const BUCKET_ORDER: Record<Bucket, number> = {
  review: 0,
  wip: 1,
  todo: 2,
  waiting: 3,
  done: 4,
};

const BUCKET_DOT: Record<Bucket, string> = {
  todo: "bg-muted-foreground",
  wip: "bg-orange-500",
  review: "bg-red-500",
  waiting: "bg-violet-500",
  done: "bg-emerald-500",
};

const BUCKET_TONE: Record<Bucket, StatusTone> = {
  todo: "gray",
  wip: "amber",
  review: "red",
  waiting: "purple",
  done: "green",
};

const PRIORITY_DOT: Record<string, string> = {
  urgent: "bg-red-500",
  high: "bg-orange-500",
  medium: "bg-yellow-500",
  low: "bg-blue-500",
  none: "bg-muted-foreground",
};

type FilterKey = "tous" | "todo" | "wip" | "review";

const FILTERS: { key: FilterKey; label: string; buckets: Bucket[] }[] = [
  { key: "tous", label: "Tous", buckets: ["review", "wip", "todo", "waiting"] },
  { key: "todo", label: "À faire", buckets: ["todo"] },
  { key: "wip", label: "En cours", buckets: ["wip"] },
  { key: "review", label: "En review", buckets: ["review"] },
];

export const MyDayTickets = () => {
  const { identity } = useGetIdentity();
  const { devTaskStatuses, devTaskPriorities } = useConfigurationContext();
  const [filter, setFilter] = useState<FilterKey>("tous");

  const { data, isPending } = useGetList<DevTask>(
    "dev_tasks",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "due_date", order: "ASC" },
      filter: {
        "assignee_ids@cs": identity?.id ? `{${identity.id}}` : undefined,
        "archived_at@is": null,
        "status@in": ACTIVE_STATUSES,
      },
    },
    { enabled: !!identity?.id },
  );

  const sorted = useMemo(() => {
    if (!data) return [] as DevTask[];
    return [...data].sort((a, b) => {
      const ba = DB_TO_BUCKET[a.status] ?? "todo";
      const bb = DB_TO_BUCKET[b.status] ?? "todo";
      const diff = BUCKET_ORDER[ba] - BUCKET_ORDER[bb];
      if (diff !== 0) return diff;
      // tri secondaire : due_date ASC (urgent en haut)
      const da = a.due_date ? new Date(a.due_date).getTime() : Infinity;
      const db = b.due_date ? new Date(b.due_date).getTime() : Infinity;
      return da - db;
    });
  }, [data]);

  const counts = useMemo<Record<FilterKey, number>>(() => {
    const tous = sorted.filter((t) => DB_TO_BUCKET[t.status] !== "done").length;
    const todo = sorted.filter((t) => DB_TO_BUCKET[t.status] === "todo").length;
    const wip = sorted.filter((t) => DB_TO_BUCKET[t.status] === "wip").length;
    const review = sorted.filter(
      (t) => DB_TO_BUCKET[t.status] === "review",
    ).length;
    return { tous, todo, wip, review };
  }, [sorted]);

  const activeFilter = FILTERS.find((f) => f.key === filter)!;
  const filtered = sorted.filter((t) =>
    activeFilter.buckets.includes(DB_TO_BUCKET[t.status] ?? "todo"),
  );

  return (
    <Card>
      <CardContent className="p-0">
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <Code2 className="size-4 text-violet-700" />
          <h2 className="text-[14px] font-semibold text-foreground">
            Mes tickets
          </h2>
          {counts.tous > 0 && (
            <span className="text-[12px] text-muted-foreground tabular-nums">
              ({counts.tous})
            </span>
          )}
          <span className="text-[12px] text-muted-foreground hidden md:inline ml-1">
            Ce que tu fais aujourd'hui.
          </span>
          <Link
            to="/dev_tasks/create"
            className="ml-auto inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[12px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60"
          >
            <Plus className="size-3.5" />
            Nouveau
          </Link>
        </div>

        {/* Filter tabs */}
        {!isPending && counts.tous > 0 && (
          <div className="flex flex-wrap items-center gap-1 px-3 py-2 border-b border-border">
            {FILTERS.map((f) => {
              const isActive = filter === f.key;
              const n = counts[f.key];
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilter(f.key)}
                  className={cn(
                    "h-7 px-2.5 rounded-md text-[12.5px] inline-flex items-center gap-1.5",
                    isActive
                      ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900"
                      : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                  )}
                >
                  {f.label}
                  <span
                    className={cn(
                      "text-[10.5px] tabular-nums",
                      isActive ? "text-white/70" : "text-muted-foreground",
                    )}
                  >
                    {n}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Body */}
        {isPending ? (
          <div className="flex flex-col gap-2 p-4">
            {[1, 2, 3].map((n) => (
              <Skeleton key={n} className="h-10 w-full" />
            ))}
          </div>
        ) : counts.tous === 0 ? (
          <EmptyState
            variant="celebrate"
            icon={<Check className="size-6" />}
            title="Aucun ticket assigné aujourd'hui"
            description="Profite du calme ou attaque la dette technique."
            ctas={
              <Link
                to="/dev_tasks/create"
                className="inline-flex items-center gap-1.5 rounded-md bg-zinc-900 text-white px-3 py-1.5 text-[13px] font-medium dark:bg-zinc-100 dark:text-zinc-900"
              >
                <Plus className="size-3.5" />
                Créer un ticket
              </Link>
            }
          />
        ) : filtered.length === 0 ? (
          <p className="text-[12.5px] text-muted-foreground p-4">
            Aucun ticket dans ce filtre.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-border/40">
            {filtered.map((task) => {
              const bucket = DB_TO_BUCKET[task.status] ?? "todo";
              const statusLabel =
                devTaskStatuses.find((s) => s.value === task.status)?.label ??
                task.status;
              const priority = devTaskPriorities.find(
                (p) => p.value === task.priority,
              );
              const dueRel = task.due_date
                ? formatDistanceToNowStrict(new Date(task.due_date), {
                    locale: fr,
                    addSuffix: true,
                  })
                : null;
              const overdue =
                task.due_date && new Date(task.due_date).getTime() < Date.now();

              return (
                <li key={task.id}>
                  <Link
                    to={`/dev_tasks/${task.id}/show`}
                    className="grid grid-cols-[12px_1fr_auto] gap-3 items-start py-3 px-4 hover:bg-muted/40"
                  >
                    <span
                      className={cn(
                        "size-2 rounded-full mt-1.5 shrink-0",
                        BUCKET_DOT[bucket],
                      )}
                      title={statusLabel}
                    />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="text-[11px] font-mono text-muted-foreground tabular-nums">
                          [#{task.id}]
                        </span>
                        <span className="text-[13.5px] font-medium text-foreground truncate">
                          {task.title}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-[11.5px]">
                        <StatusPill
                          tone={BUCKET_TONE[bucket]}
                          label={statusLabel}
                        />
                        {priority && (
                          <>
                            <span className="text-muted-foreground/50">·</span>
                            <span className="inline-flex items-center gap-1 text-muted-foreground">
                              <span
                                className={cn(
                                  "size-1.5 rounded-full",
                                  PRIORITY_DOT[task.priority] ??
                                    "bg-muted-foreground",
                                )}
                              />
                              Priorité {priority.label.toLowerCase()}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      {dueRel ? (
                        <span
                          className={cn(
                            "text-[11.5px]",
                            overdue
                              ? "text-red-600 dark:text-red-300 font-medium"
                              : "text-muted-foreground",
                          )}
                        >
                          {overdue ? "en retard" : `Échéance ${dueRel}`}
                        </span>
                      ) : (
                        <span className="text-[11.5px] text-muted-foreground/70">
                          Pas d'échéance
                        </span>
                      )}
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
